import React, { useState } from "react";
import { Seo } from "@/components/Seo";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Phase 14 — landing page for the emailed reset link (/reset-password?token=…).
// Tokens are single-use and expire after 1 hour; completing the reset revokes
// every existing session server-side.
export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (password !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.resetConfirm(token, password);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset your password — the link may have expired.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6 py-16">
      <Seo title="Reset password" />
      <Card className="w-full max-w-md border-neutral-200">
        <CardContent className="p-10">
          {done ? (
            <div data-testid="reset-success">
              <div className="font-display text-3xl text-forest tracking-tight">Password updated</div>
              <p className="text-neutral-600 mt-2 text-sm">
                All your previous sessions were signed out for safety. Sign in with your new password.
              </p>
              <Button
                data-testid="reset-goto-login-btn"
                onClick={() => nav("/login")}
                className="w-full mt-7 h-12 rounded-full bg-forest hover:bg-forest-hover text-white"
              >
                Go to sign in
              </Button>
            </div>
          ) : !token ? (
            <div data-testid="reset-no-token">
              <div className="font-display text-3xl text-forest tracking-tight">Link not valid</div>
              <p className="text-neutral-600 mt-2 text-sm">
                This page needs a reset link from your email. Request a new one from the sign-in page.
              </p>
              <Link to="/login" className="inline-block mt-6 text-sm text-forest underline underline-offset-2">Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="font-display text-3xl text-forest tracking-tight">Choose a new password</div>
              <p className="text-neutral-600 mt-2 text-sm">
                At least 8 characters, with a letter and a number. This link works once and expires after 1 hour.
              </p>
              <div className="space-y-3 mt-6">
                <Input
                  data-testid="reset-password-input"
                  type="password"
                  autoComplete="new-password"
                  placeholder="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11"
                />
                <Input
                  data-testid="reset-password-confirm-input"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Repeat new password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="h-11"
                />
              </div>
              <Button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={busy || !password || !confirm}
                className="w-full mt-5 h-12 rounded-full bg-forest hover:bg-forest-hover text-white"
              >
                {busy ? "Saving…" : "Set new password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
